import {
  ChangeDetectorRef,
  Component,
  Directive,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { logger } from '@app/core/helpers/logger';
import { ContextService, MetaService, PlatformService, UtilsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { EContentPaneFormat, EContentPanelRoute, IContentPane } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { from, fromEvent } from 'rxjs';
import { catchError, debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { AbstractPane } from '../models/abstract-pane';

// Кэш для хранения загруженных компонентов
const componentCache = new Map<EContentPaneFormat, Promise<any>>();

@Directive({
  // tslint:disable-next-line: directive-selector
  selector: '[pane-container]',
})
export class PaneContainerDirective {
  public constructor(public viewContainerRef: ViewContainerRef) {}
}

@Component({
  selector: 'app-pane',
  templateUrl: './pane.component.html',
  styleUrls: ['./pane.component.scss'],
})
export class PaneComponent extends ChangableComponent implements OnInit {
  public debug$ = this.contextService.debug$;

  @Input()
  public set item(value: IContentPane) {
    this._item = value;
    this.loadComponent();
    if (this.isServer) {
      this.loadContent();
    }
  }

  public get item(): IContentPane {
    return this._item;
  }

  @Output() public loadFinished = new EventEmitter<boolean>();

  @ViewChild(PaneContainerDirective, { static: true }) public paneContainer: PaneContainerDirective;

  private componentInstance: AbstractPane;
  private _loadContentRequested = false;
  private _item: IContentPane;
  public stuckState: boolean;

  /** Показывает для родителей, что панелька не имеет контента (хотя он может быть на самом деле) */
  private _hasContent = false;
  /** Показывает для себя, что панелька не имеет контента (значит, его нет на самом деле) */
  private _isVisible = false;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    public utilsService: UtilsService,
    protected readonly platform: PlatformService,
    protected readonly cdr: ChangeDetectorRef,
    private contextService: ContextService,
    private meta: MetaService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit() {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    if (
      this.isBrowser &&
      this.format === EContentPaneFormat['categories'] &&
      this.item.baseRoute === EContentPanelRoute.lessons &&
      this.deviceService.isMobile()
    ) {
      this.stickyEvent();
    }
    if (this.item.baseRoute) {
      this.meta.setTitle([this.item.baseRoute + '.meta-title']);
    }
  }

  public get isVisible(): boolean {
    return this._isVisible;
  }

  public get hasContent(): boolean {
    return this._hasContent;
  }

  public get format(): EContentPaneFormat {
    return this.item?.format;
  }

  private stickyEvent(): void {
    fromEvent(window, 'scroll')
      .pipe(
        debounceTime(10),
        catchError((error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          this.stuckState = false;
          return [];
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.stuckState = window.scrollY >= 125;
        this.detectChanges();
      });
  }

  public get contentLoaded(): boolean {
    return this.componentInstance?.loaded;
  }

  /**
   * NOTE Вас не бесит, что имя компоненты, соответствующий формату панели, приходится искать через этот метод?
   */
  protected async getChildByFormat(format: EContentPaneFormat): Promise<any> {
    let newFormat = format;
    if (this.deviceService.isMobile() && this.item.replace_for_mobile) {
      if (format === EContentPaneFormat['new-grid-universal-card']) {
        newFormat = EContentPaneFormat[this.item.replace_for_mobile_pane];
      }
    }

    if (componentCache.has(newFormat)) {
      return componentCache.get(newFormat);
    }

    try {
      const componentPromise = this.loadComponentByFormat(newFormat);
      componentCache.set(newFormat, componentPromise);
      return await componentPromise;
    } catch (error) {
      logger.error(`Failed to load component for format ${newFormat}`, error);
      return null;
    }
  }

  private async loadComponentByFormat(format: EContentPaneFormat): Promise<any> {
    try {
      switch (format) {
        case EContentPaneFormat['card-mini']:
        case EContentPaneFormat['card-mid']:
        case EContentPaneFormat['card-micro']:
          const { CardMiniPaneComponent } = await import('../card-mini-pane/card-mini-pane.component');
          return CardMiniPaneComponent;

        case EContentPaneFormat['banner-invite']:
          const { ComeVisitBannerComponent } = await import('../come-visit-banner/come-visit-banner.component');
          return ComeVisitBannerComponent;

        case EContentPaneFormat['banner-subject']:
          const { NewSubjectBannerComponent } = await import('../new-subject-banner/new-subject-banner.component');
          return NewSubjectBannerComponent;

        case EContentPaneFormat['teacher']:
        case EContentPaneFormat['teacher-of-the-day']:
          const { LibraryTeacherCardComponent } = await import(
            '../library-teacher-card/library-teacher-card.component'
          );
          return LibraryTeacherCardComponent;

        case EContentPaneFormat['subjects']:
        case EContentPaneFormat['study_subjects']:
          const { SubjectsContentFilterComponent } = await import(
            '../subjects-content-filter/subjects-content-filter.component'
          );
          return SubjectsContentFilterComponent;

        case EContentPaneFormat['categories']:
        case EContentPaneFormat['study_categories']:
          const { CategoriesContentFilterComponent } = await import(
            '../categories-content-filter/categories-content-filter.component'
          );
          return CategoriesContentFilterComponent;

        case EContentPaneFormat['meetings']:
          const { MeetingsDragScrollComponent } = await import(
            '../meetings-drag-scroll/meetings-drag-scroll.component'
          );
          return MeetingsDragScrollComponent;

        case EContentPaneFormat['teachers']:
          const { TeacherDragScrollComponent } = await import('../teacher-drag-scroll/teacher-drag-scroll.component');
          return TeacherDragScrollComponent;

        case EContentPaneFormat['recent']:
        case EContentPaneFormat['last-created']:
          const { NewContentComponent } = await import('../new-content/new-content.component');
          return NewContentComponent;

        case EContentPaneFormat['gallery']:
          const { GalleryDragScrollComponent } = await import('../gallery-drag-scroll/gallery-drag-scroll.component');
          return GalleryDragScrollComponent;

        case EContentPaneFormat['goback']:
          const { GobackPaneComponent } = await import('../goback-pane/goback-pane.component');
          return GobackPaneComponent;

        case EContentPaneFormat['card-universal']:
        case EContentPaneFormat['last-viewed']:
        case EContentPaneFormat['popular']:
          const { CardUniversalComponent } = await import('../card-universal/card-universal.component');
          return CardUniversalComponent;

        case EContentPaneFormat['card-full-main']:
          const { CardFullMainComponent } = await import('../card-full-main/card-full-main.component');
          return CardFullMainComponent;

        case EContentPaneFormat['card-full']:
        case EContentPaneFormat['lesson-of-the-day']:
          const { CardFullComponent } = await import('../card-full/card-full.component');
          return CardFullComponent;

        case EContentPaneFormat['exercises']:
          const { ExercisesDragScrollComponent } = await import(
            '../exercises-drag-scroll/exercises-drag-scroll.component'
          );
          return ExercisesDragScrollComponent;

        case EContentPaneFormat['informer-stats']:
          const { StatisticInformerComponent } = await import('../statistic-informer/statistic-informer.component');
          return StatisticInformerComponent;

        case EContentPaneFormat['welcome']:
          const { WelcomeComponent } = await import('../welcome/welcome.component');
          return WelcomeComponent;

        case EContentPaneFormat['get-started-widget']:
          const { GetStartedWidgetComponent } = await import('../get-started-widget/get-started-widget.component');
          return GetStartedWidgetComponent;

        case EContentPaneFormat['teachers-drag-scroll']:
          const { NewTeachersComponent } = await import('../new-teachers/new-teachers.component');
          return NewTeachersComponent;

        case EContentPaneFormat['share-link-widget']:
          const { ShareLinkWidgetComponent } = await import('../share-link-widget/share-link-widget.component');
          return ShareLinkWidgetComponent;

        case EContentPaneFormat['user-services-drag-scroll']:
          const { ServicesDragScrollComponent } = await import(
            '../services-drag-scroll/services-drag-scroll.component'
          );
          return ServicesDragScrollComponent;

        case EContentPaneFormat['teachers_column']:
          const { TeachersColumnComponent } = await import('../teachers-column/teachers-column.component');
          return TeachersColumnComponent;

        case EContentPaneFormat['bread-crumbs']:
          const { BreadCrumbsComponent } = await import('../bread-crumbs/bread-crumbs.component');
          return BreadCrumbsComponent;

        case EContentPaneFormat['collections-drag-scroll']:
          const { CollectionsDragScrollComponent } = await import(
            '../collections-drag-scroll/collections-drag-scroll.component'
          );
          return CollectionsDragScrollComponent;

        case EContentPaneFormat['webinar-widget']:
          const { WebinarWidgetComponent } = await import('../webinar-widget/webinar-widget.component');
          return WebinarWidgetComponent;

        case EContentPaneFormat['base-pages']:
          const { BasePagesComponent } = await import('../base-pages/base-pages.component');
          return BasePagesComponent;

        case EContentPaneFormat['meetings-stack']:
          const { MeetingsStackComponent } = await import('../meetings-stack/meetings-stack.component');
          return MeetingsStackComponent;

        case EContentPaneFormat['user-services-stack']:
          const { UserServicesStackComponent } = await import('../user-services-stack/user-services-stack.component');
          return UserServicesStackComponent;

        case EContentPaneFormat['schools-column']:
          const { SchoolsColumnComponent } = await import('../schools-column/schools-column.component');
          return SchoolsColumnComponent;

        case EContentPaneFormat['my-classes-schedule']:
          const { MyClassesScheduleComponent } = await import('../my-classes-schedule/my-classes-schedule.component');
          return MyClassesScheduleComponent;

        case EContentPaneFormat['my-classes-tutors']:
          const { MyClassesTutorsComponent } = await import('../my-classes-tutors/my-classes-tutors.component');
          return MyClassesTutorsComponent;

        case EContentPaneFormat['my-classes-classrooms']:
          const { MyClassesClassroomsComponent } = await import(
            '../my-classes-classrooms/my-classes-classrooms.component'
          );
          return MyClassesClassroomsComponent;

        case EContentPaneFormat['my-classes-homework']:
          const { MyClassesHomeworkComponent } = await import('../my-classes-homework/my-classes-homework.component');
          return MyClassesHomeworkComponent;

        case EContentPaneFormat['my-classes-students']:
          const { MyClassesStudentsComponent } = await import('../my-classes-students/my-classes-students.component');
          return MyClassesStudentsComponent;

        case EContentPaneFormat['my-classes-schools']:
          const { MyClassesSchoolsComponent } = await import('../my-classes-schools/my-classes-schools.component');
          return MyClassesSchoolsComponent;

        case EContentPaneFormat['my-library-content']:
          const { MyLibraryContentComponent } = await import('../my-library-content/my-library-content.component');
          return MyLibraryContentComponent;

        case EContentPaneFormat['my-library-services']:
          const { MyLibraryServicesComponent } = await import('../my-library-services/my-library-services.component');
          return MyLibraryServicesComponent;

        case EContentPaneFormat['my-library-collections']:
          const { MyLibraryCollectionsComponent } = await import(
            '../my-library-collections/my-library-collections.component'
          );
          return MyLibraryCollectionsComponent;

        case EContentPaneFormat['my-library-courses']:
          const { MyLibraryCoursesComponent } = await import('../my-library-courses/my-library-courses.component');
          return MyLibraryCoursesComponent;

        case EContentPaneFormat['comments']:
          const { MyCommentsComponent } = await import('../my-comments/my-comments.component');
          return MyCommentsComponent;

        case EContentPaneFormat['people-news']:
          const { PeopleNewsComponent } = await import('../people-news/people-news.component');
          return PeopleNewsComponent;

        case EContentPaneFormat['classroom-news']:
          const { ClassroomNewsComponent } = await import('../classroom-news/classroom-news.component');
          return ClassroomNewsComponent;

        case EContentPaneFormat['schools-news']:
          const { SchoolsNewsComponent } = await import('../schools-news/schools-news.component');
          return SchoolsNewsComponent;

        case EContentPaneFormat['favorites-lessons']:
          const { FavoritesLessonsComponent } = await import('../favorites-lessons/favorites-lessons.component');
          return FavoritesLessonsComponent;

        case EContentPaneFormat['favorites-services']:
          const { FavoritesServicesComponent } = await import('../favorites-services/favorites-services.component');
          return FavoritesServicesComponent;

        case EContentPaneFormat['favorites-tutors']:
          const { FavoritesTutorsComponent } = await import('../favorites-tutors/favorites-tutors.component');
          return FavoritesTutorsComponent;

        case EContentPaneFormat['favorites-collections']:
          const { FavoritesCollectionsComponent } = await import(
            '../favorites-collections/favorites-collections.component'
          );
          return FavoritesCollectionsComponent;

        case EContentPaneFormat['favorites-users']:
          const { FavoritesUsersComponent } = await import('../favorites-users/favorites-users.component');
          return FavoritesUsersComponent;

        case EContentPaneFormat['favorites-schools']:
          const { FavoritesSchoolsComponent } = await import('../favorites-schools/favorites-schools.component');
          return FavoritesSchoolsComponent;

        case EContentPaneFormat['widget-welcome']:
          const { WidgetWelcomeComponent } = await import('../widgets/widget-welcome/widget-welcome.component');
          return WidgetWelcomeComponent;

        case EContentPaneFormat['widget-filter']:
          const { WidgetFilterComponent } = await import('../widgets/widget-filter/widget-filter.component');
          return WidgetFilterComponent;

        case EContentPaneFormat['widget-news']:
          const { WidgetNewsComponent } = await import('../widgets/widget-news/widget-news.component');
          return WidgetNewsComponent;

        case EContentPaneFormat['widget-onboarding']:
          const { WidgetOnboardingComponent } = await import(
            '../widgets/widget-onboarding/widget-onboarding.component'
          );
          return WidgetOnboardingComponent;

        case EContentPaneFormat['widget-popular-lessons']:
          const { WidgetPopularLessonsComponent } = await import(
            '../widgets/widget-popular-lessons/widget-popular-lessons.component'
          );
          return WidgetPopularLessonsComponent;

        case EContentPaneFormat['widget-popular-meetings']:
          const { WidgetPopularMeetingsComponent } = await import(
            '../widgets/widget-popular-meetings/widget-popular-meetings.component'
          );
          return WidgetPopularMeetingsComponent;

        case EContentPaneFormat['widget-popular-tutors']:
          const { WidgetPopularTutorsComponent } = await import(
            '../widgets/widget-popular-tutors/widget-popular-tutors.component'
          );
          return WidgetPopularTutorsComponent;

        case EContentPaneFormat['new-grid-universal-card']:
          const { GridCardUniversalComponent } = await import('../grid-card-universal/grid-card-universal.component');
          return GridCardUniversalComponent;

        case EContentPaneFormat['new-exercises']:
          const { NewGridExercisesComponent } = await import('../new-grid-exercises/new-grid-exercises.component');
          return NewGridExercisesComponent;

        case EContentPaneFormat['new-simple-title']:
          const { NewSimpleTitleComponent } = await import('../new-simple-title/new-simple-title.component');
          return NewSimpleTitleComponent;

        default:
          return null;
      }
    } catch (error) {
      logger.error(`Failed to load component for format ${format}`, error);
      return null;
    }
  }

  public loadComponent(): void {
    if (!this._item) {
      return;
    }

    from(this.getChildByFormat(this._item.format))
      .pipe(
        catchError((error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          logger.error('Failed to load component', error);
          return [];
        }),
        finalize(() => {
          this._loadContentRequested = false;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((ngItem) => {
        if (!ngItem || !this.paneContainer) {
          logger.warn('Not supported <content-pane> form ' + this._item.format);
          this._hasContent = false;
          return;
        }

        const ref = this.paneContainer.viewContainerRef;
        ref.clear();
        const componentRef = ref.createComponent<AbstractPane>(ngItem);
        this.componentInstance = componentRef.instance;
        this.componentInstance.pane = this._item;

        this.setupComponentSubscription();
        this.componentInstance.load();
      });
  }

  private setupComponentSubscription(): void {
    if (!this.componentInstance) {
      return;
    }

    if (this._item.baseRoute === EContentPanelRoute['my-classes/dashboard']) {
      this._isVisible = true;
    }

    this.componentInstance.loadFinished
      .pipe(
        catchError((error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          logger.error('Failed to load component content', error);
          return [];
        }),
        finalize(() => {
          this._loadContentRequested = false;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((hasContent) => {
        if (this.componentInstance) {
          this._isVisible = this.componentInstance.isVisible;
          const ignoreContent = !!this._item.always_think_as_ampty;
          this._hasContent = (!ignoreContent && hasContent) || ignoreContent;
          this.loadFinished.emit(this._hasContent);
        }
      });
  }

  protected loadContent(): void {
    if (this.componentInstance && !this._loadContentRequested && this.componentInstance.load) {
      this._loadContentRequested = true;
      this.componentInstance.load();
    }
  }

  public onInViewportTopChange(inViewport: { visible: boolean }): void {
    if (inViewport.visible) {
      this.loadContent();
    }
  }

  public onInViewportBottomChange(inViewport: { visible: boolean }): void {
    if (inViewport.visible) {
      this.loadContent();
    }
  }

  public showMoreInfoDebug(): void {
    logger.log('More info about featuring pane #' + this._item.order, this._item);
  }
}
